import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@application/store/useStore';
import { getAllInterestsWithoutPaginationAsync } from '@application/features/interests/interestsThunk';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import HSingleDatePicker from '@ui/components/common/Pickers/HDatePicker';
import { toast } from 'react-toastify';
import { createExternalRelationAsync, getExternalRelationByIdAsync, updateExternalRelationAsync } from '@application/features/externalRelations/externalRelationsThunk';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { GenderEnum } from '@application/enums/gender-enum';
import { isZeroDate } from '@application/utilities/date-utils';
const AddNewRelationForm = ({ onClose, formRef, edit = false, relationId }) => {
    const { t } = useTranslation();
    const { control, handleSubmit, formState: { errors }, setValue, reset } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phoneNo: '',
            company: '',
            gender: '',
            designation: '',
            referredBy: '',
            interests: []
        }
    });
    const { selectedCompanyId } = useSelector((state) => state.company);
    const { interestswithoutpagination } = useSelector((state) => state.interests);
    const { peopleForTasks } = useSelector((state) => state.people);
    const { selectedExternalRelation } = useSelector((state) => state.externalRelations);
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            dispatch(getAllInterestsWithoutPaginationAsync());
            dispatch(getAllPeopleForTasksAsync());
        }
    }, [selectedCompanyId]);
    // Mock options - replace with actual API data
    const genderOptions = [
        { label: t('gender.male'), value: GenderEnum.Male },
        { label: t('gender.female'), value: GenderEnum.Female }
    ];
    const referredByOptions = peopleForTasks?.map((people) => ({
        label: `${people.name} (${people.peopleCompany})`,
        value: people.id
    })) ?? [];
    // Transform Interests for InterestDropdown
    const interestOptions = interestswithoutpagination?.map((interest) => ({
        label: interest.title,
        value: interest.id
    })) ?? [];
    useEffect(() => {
        if (relationId) {
            dispatch(getExternalRelationByIdAsync(relationId))
                .unwrap()
                .then((response) => {
                setValue('company', response.company || '');
                let dateOfBirth = undefined;
                if (response.dob && !isZeroDate(response.dob)) {
                    const dob = new Date(response.dob);
                    if (!isNaN(dob.getTime())) {
                        dateOfBirth = dob;
                    }
                }
                setValue('dateOfBirth', dateOfBirth);
                setValue('designation', response.designation || '');
                setValue('email', response.email);
                setValue('firstName', response.firstName);
                setValue('lastName', response.lastName);
                setValue('gender', response.gender || '');
                setValue('phoneNo', response.phoneNo || '');
                setValue('referredBy', response.referredBy || '');
                const preselectedInterests = response.interests
                    ?.map((interestDTO) => {
                    return interestOptions.find((option) => option.value === interestDTO.id);
                })
                    .filter((option) => option !== undefined);
                setValue('interests', preselectedInterests);
            });
        }
    }, [edit, relationId]);
    const normalizeField = (value) => {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed === '' ? undefined : trimmed;
        }
        return value ?? undefined;
    };
    const onSubmit = async (data) => {
        {
            const formattedDOB = data.dateOfBirth ? new Date(data.dateOfBirth).toISOString() : null;
            const interestsGuids = data.interests?.map((option) => option.value);
            // UPDATE EXISTING RELATION
            if (edit && selectedExternalRelation) {
                const updateData = {
                    id: selectedExternalRelation.id,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    email: data.email,
                    phoneNo: normalizeField(data.phoneNo),
                    company: normalizeField(data.company),
                    gender: normalizeField(data.gender),
                    designation: normalizeField(data.designation),
                    referredBy: normalizeField(data.referredBy),
                    interests: interestsGuids?.length ? interestsGuids : undefined,
                    dob: formattedDOB ? formattedDOB : undefined
                };
                await dispatch(updateExternalRelationAsync(updateData))
                    .unwrap()
                    .then((responseData) => {
                    toast.success(t('relations.relationUpdatedSuccessfully'));
                })
                    .catch((backendMessage) => {
                    toast.error(t('relations.relationUpdatedFailed'));
                });
                if (onClose)
                    onClose();
                return;
            }
            // CREATE NEW RELATION
            const createData = {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phoneNo: normalizeField(data.phoneNo),
                company: normalizeField(data.company),
                gender: normalizeField(data.gender),
                designation: normalizeField(data.designation),
                referredBy: normalizeField(data.referredBy),
                interests: interestsGuids?.length ? interestsGuids : undefined,
                dob: formattedDOB ? formattedDOB : undefined
            };
            await dispatch(createExternalRelationAsync(createData))
                .unwrap()
                .then((response) => {
                response.isSuccess == true
                    ? toast.success(response.message || t('relations.relationCreatedSuccessfully'))
                    : toast.error(response.message || t('relations.relationCreationfailed'));
            })
                .catch(() => {
                toast.error(t('relations.relationCreationfailed'));
            });
            if (onClose)
                onClose();
            // reset form fields after creation
            reset({
                firstName: '',
                lastName: '',
                email: '',
                phoneNo: '',
                company: '',
                gender: '',
                designation: '',
                referredBy: '',
                interests: [],
                dateOfBirth: undefined
            });
        }
    };
    return (_jsx("form", { ref: formRef, onSubmit: handleSubmit(onSubmit), children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'firstName', label: t('relations.firstName'), rules: {
                        required: t('validations.required', { field: t('relations.firstName') })
                    }, errorMessage: errors.firstName?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'lastName', label: t('relations.lastName'), rules: {
                        required: t('validations.required', { field: t('relations.lastName') })
                    }, errorMessage: errors.lastName?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, rules: {
                        required: t('validations.required', { field: t('relations.email') }),
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: t('validations.invalidEmail', {
                                field: t('relations.email')
                            })
                        }
                    }, errorMessage: errors.email?.message, name: 'email', label: t('relations.email'), disabled: edit }), _jsx(PhoneInputMasking, { bg: 'grey8', control: control, label: t('relations.phoneNumber'), name: 'phoneNo', error: errors.phoneNo?.message }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'gender', label: t('relations.gender'), options: genderOptions, error: errors.gender?.message }), _jsx(Controller, { control: control, name: 'dateOfBirth', 
                    // rules={{
                    //   required: t('validations.required', { field: t('family.dateOfBirth') }) as string,
                    //   validate: (value) => {
                    //     if (!value)
                    //       return t('validations.required', { field: t('family.dateOfBirth') }) as string;
                    //     const birthDate = new Date(value);
                    //     if (isNaN(birthDate.getTime())) {
                    //       return t('validations.invalidDate', { field: t('family.dateOfBirth') }) as string;
                    //     }
                    //     const today = new Date();
                    //     const age = today.getFullYear() - birthDate.getFullYear();
                    //     if (birthDate > today) {
                    //       return t('validations.futureDate', { field: t('family.dateOfBirth') }) as string;
                    //     }
                    //     if (age > 150) {
                    //       return t('validations.maxAge', {
                    //         field: t('family.dateOfBirth'),
                    //         max: 150
                    //       }) as string;
                    //     }
                    //     if (age < 0) {
                    //       return t('validations.invalidDate', { field: t('family.dateOfBirth') }) as string;
                    //     }
                    //     return true;
                    //   }
                    // }}
                    render: ({ field }) => {
                        const getDateValue = () => {
                            if (!field.value)
                                return undefined;
                            if (field.value instanceof Date && !isNaN(field.value.getTime())) {
                                return field.value.toISOString().split('T')[0];
                            }
                            if (typeof field.value === 'string') {
                                const parsedDate = new Date(field.value);
                                if (!isNaN(parsedDate.getTime())) {
                                    return parsedDate.toISOString().split('T')[0];
                                }
                            }
                            return undefined;
                        };
                        return (_jsx(HSingleDatePicker, { value: getDateValue(), bg: 'grey8', handleSelectedDate: (date) => field.onChange(date ? new Date(date) : null), label: t('family.dateOfBirth'), minDate: new Date('1900-01-01'), maxDate: new Date() }));
                    } }), errors.dateOfBirth && (_jsx(Typography, { variant: 'caption', color: 'error', sx: { mt: -1, mb: 1 }, children: errors.dateOfBirth.message })), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'company', label: t('relations.company'), 
                    // rules={{
                    //   required: t<string>('validations.required', { field: t<string>('relations.company') })
                    // }}
                    errorMessage: errors.company?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'designation', label: t('relations.designation'), 
                    // rules={{
                    //   required: t<string>('validations.required', { field: t<string>('relations.designation') })
                    // }}
                    errorMessage: errors.designation?.message }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'referredBy', label: t('relations.referredBy'), options: referredByOptions, error: errors.referredBy?.message }), _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', name: 'interests', control: control, options: interestOptions, label: t('relations.tags'), error: errors.interests?.message, getOptionKey: (option) => option.value, showSelectAll: true, isSortable: true, selectAllLabel: t('actions.selectAll') })] }) }));
};
export default AddNewRelationForm;
